import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import guidance from "../assets/img/HomePage/guidance.svg";
import ModalAbout from "../components/ModalAbout";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import Options from "../components/Options";
import caretDown from "../assets/img/Entities/caretDown.svg";
import caretRight from "../assets/img/Entities/caretRight.svg";
import caretUrdu from "../assets/img/Entities/caretUrdu.svg";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { changeTab } from "../redux/TabSlice";
import { setSelectedOffence, getOrganizationsByOffence } from "../redux/OrganizationSlice";
import { getSelectedLaw, getSelectedGuidance } from "../redux/OffenceSlice";
import { getTitleFromAppContent, getTranslation } from "../helpers/utils";
import { trackGuidanceView, trackPageView } from "../helpers/api";
import { colors } from "../constants/theme";
import PageBackButton from "../components/PageBackButton";
import LinkifiedText from "../components/LinkifiedText";
import { setBackToGuide } from "../redux/PageSlice";
import { setSelectedProviderEng } from "../redux/AppContentSlice";
import { guidanceOffenceHasFullSteps } from "../helpers/guidanceHelpers";

const useStyles = makeStyles((theme) => ({
  bgcolor: {
    background: "#FFFFFF",
  },
  textColor: {
    color: theme.palette.guidance.main,
  },
  input: {
    "&::placeholder": {
      color: theme.palette.guidance.main,
    },
  },

  sectionbg: {
    background: theme.palette.primary.main,
  },

  sectionBgLight: {
    backgroundColor: colors.lawsLightBg,
  },

  guidanceBg: {
    backgroundColor: colors.guidanceLightBg,
  },

  guidanceTextBg: {
    backgroundColor: theme.palette.guidance.main,
  },
  fullScreen: {
    minHeight: "100vh",
  },
  height: {
    maxHeight: "50vh",
  },
}));

function GuideStep() {
  const classes = useStyles();
  const { selectedGuide } = useSelector((state) => state.offence);
  const selectedGuidanceLoading = useSelector(
    (state) => state.offence.selectedGuidanceLoading
  );
  const { appContent } = useSelector((state) => state.appContent);
  const { selLanguageId, isEnglish, langId } = useSelector((state) => state.language);
  const { selDistrictId } = useSelector((state) => state.district);
  const { selProvinceId } = useSelector((state) => state.province);
  const [openAbout, setOpenAbout] = useState(false);
  const [guidanceTitle, setGuidanceTitle] = useState("");
  const [stepsTitle, setStepsTitle] = useState("");
  const [relevantLawTitle, setRelevantLawTitle] = useState("");
  const [supportOrgsTitle, setSupportOrgsTitle] = useState("");
  const [youtubeLinkText, setYoutubeLinkText] = useState("");
  const [dropdown, setDropdown] = useState(0);
  const [lawLoading, setLawLoading] = useState(false);
  const handleOpen = () => setOpenAbout(true);
  const handleClose = () => setOpenAbout(false);

  useEffect(() => {
    trackPageView("Step by Step Guidance", { province: selProvinceId, district: selDistrictId, language: selLanguageId });
  }, [selProvinceId, selDistrictId, selLanguageId]);

  // CMS values sometimes paste numbered list text (e.g. "1. https://youtu.be/...")
  // which makes window.open treat the string as a site-relative path.
  const extractFirstAbsoluteUrl = (raw) => {
    if (raw == null || raw === "") return "";
    const s = String(raw).trim();
    const m = s.match(/https?:\/\/[^\s]+/i);
    return m ? m[0] : s;
  };

  // Helper function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const cleaned = extractFirstAbsoluteUrl(url);
    // Handle various YouTube URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = cleaned.match(regExp);
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    return cleaned || url; // Return cleaned or original if not a standard YouTube URL
  };

  // Get video URL from first guidance step that has a video (only guidance steps have videos)
  const rawVideoUrl = selectedGuide?.guidance_steps?.find(step => step?.video_url)?.video_url;
  const videoUrl = rawVideoUrl ? extractFirstAbsoluteUrl(rawVideoUrl) : "";

  // Helper to safely extract text from Translation objects
  const getTextFromTranslation = (translation) => {
    if (!translation) return '';
    if (typeof translation === 'string') return translation;
    if (Array.isArray(translation)) {
      const result = getTranslation(translation, langId);
      if (result && typeof result === 'string') return result;
      if (translation.length > 0 && translation[0]?.text && typeof translation[0].text === 'string') {
        return translation[0].text;
      }
      return '';
    }
    if (typeof translation === 'object' && translation.text) {
      return typeof translation.text === 'string' ? translation.text : '';
    }
    return '';
  };

  const [tabSelect, setTabSelect] = useState("GuideStep");
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(changeTab(tabSelect));
  }, [tabSelect, dispatch]);

  // Track guidance view for admin dashboard
  useEffect(() => {
    if (selectedGuide?._id) {
      trackGuidanceView(selectedGuide._id, {
        province: selProvinceId,
        district: selDistrictId,
        language: selLanguageId,
      });
    }
  }, [selectedGuide?._id, selProvinceId, selDistrictId, selLanguageId]);

  // Fetch full guidance when the slim list row has no step bodies yet
  useEffect(() => {
    if (selectedGuide?._id && !guidanceOffenceHasFullSteps(selectedGuide)) {
      dispatch(
        getSelectedGuidance({
          id: selectedGuide._id,
          district: selDistrictId || undefined,
          langId: selLanguageId,
        })
      );
    }
  }, [selectedGuide, selDistrictId, selLanguageId, dispatch]);

  const stepsLoading =
    selectedGuidanceLoading ||
    (selectedGuide && !guidanceOffenceHasFullSteps(selectedGuide));

  // Pre-fetch organizations for this offence so ReferralDirectory has data when user taps Support button
  useEffect(() => {
    if (selectedGuide?._id) {
      const offenceId = typeof selectedGuide._id === "string" ? selectedGuide._id : selectedGuide._id?.toString?.();
      if (offenceId) {
        dispatch(getOrganizationsByOffence({
          offenceId,
          provinceId: selProvinceId,
          districtId: selDistrictId,
          langId: selLanguageId,
        }));
      }
    }
  }, [selectedGuide?._id, selProvinceId, selDistrictId, selLanguageId, dispatch]);

  useEffect(() => {
    if (appContent) {
      setGuidanceTitle(getTitleFromAppContent(appContent, "Step by Step Guidance", selLanguageId));
      setStepsTitle(getTitleFromAppContent(appContent, "Steps", selLanguageId));
      setRelevantLawTitle(
        getTitleFromAppContent(appContent, "Relevant Law", selLanguageId) ||
          getTitleFromAppContent(appContent, "Relevant Laws", selLanguageId)
      );
      setSupportOrgsTitle(
        getTitleFromAppContent(appContent, "Support Providing Organizations", selLanguageId) ||
          getTitleFromAppContent(appContent, "Referral", selLanguageId) ||
          getTitleFromAppContent(appContent, "Referral Guide", selLanguageId)
      );
      setYoutubeLinkText(getTitleFromAppContent(appContent, "Click to watch on YouTube", selLanguageId));
    }
  }, [appContent, selLanguageId, isEnglish, selectedGuide]);

  const handleDropdown = (id) => {
    if (id === dropdown) {
      setDropdown(0);
    } else {
      setDropdown(id);
    }
  };

  if (!selectedGuide)
    return (
      <div className={`${classes.guidanceBg} pb-20 flex flex-col`}>
        <div>
          <Header />
          <PageBackButton onClick={() => setTabSelect("StepGuide")} />
          <div className="flex-col md:flex-row flex justify-between">
            <div className=" block md:hidden mb-5">
              <Options handleOpen={handleOpen} />
            </div>
            <div className="flex mx-5 mt-2 content-center items-center" />
            <div className=" hidden md:block">
              <Options handleOpen={handleOpen} />
            </div>
          </div>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              py: 6,
              px: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Loading step-by-step guidance… If this lasts more than a few seconds, go back and open the topic again, or check your connection.
            </Typography>
          </Box>
        </div>
        <div>
          <ModalAbout open={openAbout} handleClose={handleClose} head="About" />
        </div>
      </div>
    );

  return (
    <div
      className={`${classes.guidanceBg} pb-20 flex flex-col`}
    >
      <div>
        <Header />
        <PageBackButton onClick={() => setTabSelect("StepGuide")} />
        <div className="flex-col md:flex-row flex justify-between">
          <div className=" block md:hidden mb-5">
            <Options handleOpen={handleOpen} />
          </div>
          <div className="flex mx-5 mt-2 content-center items-center">
            <img className="w-6 mr-3 mb-2" src={guidance} alt="Emergency" />
            <div className="flex flex-col md:flex-row">
              <Typography
                onClick={() => setTabSelect("StepGuide")}
                className="mr-3 cursor-pointer"
                color={"black"}
                variant="p"
              >
                {getTextFromTranslation(guidanceTitle)} {">"}
              </Typography>
              <Typography className="font-bold" color={"black"} variant="p">
                {getTextFromTranslation(selectedGuide?.title)}
              </Typography>
            </div>
          </div>
          <div className=" hidden md:block">
            <Options handleOpen={handleOpen} />
          </div>
        </div>
        <div className="flex flex-col justify-center content-center items-center my-2 w-full px-3 sm:px-0 sm:w-72 md:w-6/12 mx-auto max-w-full">
          <Typography
            className={`${classes.textColor} my-4 text-center`}
            variant="p"
          >
            {getTextFromTranslation(selectedGuide?.description)}
          </Typography>
        </div>
        <div>
          <Typography
            className={`w-full block text-center ${classes.guidanceTextBg} p-4 font-bold`}
            color={"white"}
            variant="p"
          >
            {getTextFromTranslation(stepsTitle)}
          </Typography>
        </div>
        <div className={`${classes.guidanceBg} ${classes.guidanceBg} mb-20`}>
          {stepsLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                py: 6,
                px: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Loading steps…
              </Typography>
            </Box>
          ) : (
            selectedGuide?.guidance_steps?.map((step) => (
            <div key={step._id}>
              <div
                onClick={() => handleDropdown(step._id)}
                className={clsx(
                  "flex cursor-pointer w-full px-3 sm:px-0 sm:w-72 md:w-8/12 mx-auto max-w-full",
                  isEnglish ? "flex-row" : "flex-row-reverse"
                )}
              >
                <img
                  className=" w-4"
                  src={
                    step._id === dropdown
                      ? caretDown
                      : isEnglish
                      ? caretRight
                      : caretUrdu
                  }
                  alt="Caret"
                />
                <Typography
                  className={`${classes.textColor} p-4 font-bold`}
                  variant="p"
                >
                  {getTextFromTranslation(step.title)}
                </Typography>
              </div>

              {dropdown === step._id && (
                <div className="w-full bg-white flex flex-col justify-center py-4 pb-6 items-center">
                  {step?.guidance_step_description?.map((stepDes, index) => (
                    <div
                      key={stepDes._id}
                      className="w-full px-3 sm:px-0 sm:w-72 md:w-8/12 mx-auto max-w-full mt-2"
                    >
                      <div
                        className={clsx(
                          "flex",
                          isEnglish ? "flex-row" : "flex-row-reverse"
                        )}
                      >
                        <Typography
                          className={clsx(classes.textColor, "block mx-2")}
                          variant="p"
                        >
                          {index + 1})
                        </Typography>
                        <Typography
                          className={clsx(
                            classes.textColor,
                            "mb-3 block",
                            isEnglish ? " text-left" : " text-right"
                          )}
                          variant="p"
                          component="div"
                        >
                          <LinkifiedText
                            text={getTextFromTranslation(stepDes?.description)}
                          />
                        </Typography>
                      </div>
                    </div>
                  ))}
                  
                  {/* YouTube Video Embed */}
                  {step?.video_url && (
                    <div className="w-full px-3 sm:px-0 sm:w-72 md:w-8/12 mx-auto max-w-full mt-4">
                      <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg shadow-lg">
                        <iframe
                          className="absolute top-0 left-0 w-full h-full"
                          src={getYouTubeEmbedUrl(step.video_url)}
                          title="Video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
          )}
        </div>

        {/* YouTube Video - Above Action Buttons; hidden while a step accordion is open (avoids duplicate in-step embed) */}
        {!stepsLoading && videoUrl && !dropdown && (
          <div className="w-full px-3 sm:px-0 sm:w-72 md:w-8/12 mx-auto max-w-full my-6">
            <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg shadow-lg">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={getYouTubeEmbedUrl(videoUrl)}
                title="Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <Typography
              className="text-center mt-3 text-sm text-gray-600 cursor-pointer hover:text-gray-800"
              onClick={() => window.open(videoUrl, '_blank')}
            >
              {getTextFromTranslation(youtubeLinkText) || 'Click to watch on YouTube'}
            </Typography>
          </div>
        )}

        {/* Action Buttons: match home card colors (Support = primary/referral, Relevant Law = laws) */}
        {!stepsLoading && (
        <div className="w-full px-3 sm:px-0 sm:w-72 md:w-8/12 mx-auto max-w-full my-4 flex flex-col gap-3">
          {/* Support Providing Organizations - same as home Support card (primary) */}
          <Button
            variant="contained"
            onClick={() => {
              // Set back navigation to return to GuideStep
              dispatch(setBackToGuide(true));
              dispatch(setSelectedProviderEng("GuideStep"));
              // Set the offence ID for filtering organizations
              dispatch(setSelectedOffence(selectedGuide?._id));
              dispatch(changeTab("ReferralDirectory"));
            }}
            sx={{
              backgroundColor: colors.primary,
              "&:hover": { backgroundColor: colors.primaryDark },
              borderRadius: "8px",
              padding: "14px 20px",
              width: "100%",
              justifyContent: "center",
            }}
          >
            <span className="font-medium">
              {getTextFromTranslation(supportOrgsTitle)}
            </span>
          </Button>
          {/* Relevant Law - same as home Relevant Laws card (laws purple) */}
          <Button
            variant="outlined"
            disabled={lawLoading}
            onClick={() => {
              const offenceId = selectedGuide?._id;
              if (!offenceId) return;
              dispatch(setBackToGuide(true));
              setLawLoading(true);
              dispatch(getSelectedLaw({ id: offenceId, langId: selLanguageId }))
                .unwrap()
                .then((payload) => {
                  const list = Array.isArray(payload?.res) ? payload.res : (payload?.res != null ? [payload.res] : []);
                  if (list.length > 0) {
                    dispatch(changeTab("RelevantLaws"));
                  }
                })
                .catch(() => {})
                .finally(() => setLawLoading(false));
            }}
            sx={{
              borderColor: colors.lawsMain,
              color: colors.lawsMain,
              "&:hover": {
                borderColor: colors.lawsMain,
                backgroundColor: "rgba(129, 110, 175, 0.1)",
              },
              borderRadius: "8px",
              padding: "14px 20px",
              width: "100%",
              justifyContent: "center",
            }}
          >
            <span className="text-center w-full font-medium">
              {getTextFromTranslation(relevantLawTitle)}
            </span>
          </Button>
        </div>
        )}
      </div>
      <div>
        <ModalAbout open={openAbout} handleClose={handleClose} head="About" />
      </div>
    </div>
  );
}

export default GuideStep;
